/**
 * DNACore V4 — Header Global (Onda 2)
 *
 * Componente único usado por toda página V4. Padroniza:
 *   • Breadcrumb (derivado do pathname)
 *   • Busca Global (Ctrl/⌘ K)
 *   • Favoritos (dropdown leve)
 *   • Alertas (badge + drawer)
 *   • Perfil (avatar + menu)
 *
 * 100% aditivo: dispara eventos para o DnaCommandLayer existente.
 * Páginas opt-in adicionando <DnaHeaderV4 /> no topo.
 */
import { auditLogout } from '@/lib/auth';
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, Bell, Star, ChevronRight, ChevronDown, LogOut, Settings as SettingsIcon, Home, Building2, Calendar, Filter } from 'lucide-react';
import api from '../lib/api';
import './DnaHeaderV4.css';

function readUser() {
  try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
}

// Mapa de labels para slugs comuns nas rotas V2/V3
const SLUG_LABELS = {
  v2: 'Plataforma',
  v3: 'Centro Neural',
  v4: 'V4',
  academico: 'Acadêmico',
  financeiro: 'Financeiro',
  secretaria: 'Equipe',
  eventos: 'Eventos',
  pedagogico: 'Pedagógico',
  'dna-mapa': 'DNA Mapa',
  comunicacoes: 'Comunicação',
  pedagogico: 'Pedagógico',
  ia: 'IA',
  admin: 'Configurações',
  relatorios: 'Relatórios',
  'lista-chamada': 'Lista de Chamada',
  'desafio-campeoes': 'Desafio dos Campeões',
  dnapay: 'DNAPay',
  titulos: 'Títulos',
  cobranca: 'Cobrança',
  'central-notas': 'Central de Notas',
  design: 'Design System',
  health: 'Health Check',
  audit: 'Auditoria',
  sidebar: 'Sidebar',
};

function buildCrumbs(pathname) {
  const parts = pathname.split('/').filter(Boolean);
  const crumbs = [{ label: 'Home', path: '/v3', icon: Home }];
  let acc = '';
  parts.forEach((p) => {
    acc += '/' + p;
    const label = SLUG_LABELS[p] || (/^[0-9a-f-]{16,}$/i.test(p) ? 'Detalhes' : p.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()));
    crumbs.push({ label, path: acc });
  });
  return crumbs;
}

// ─── Selector Pill ───────────────────────────────────────────
const SelectorPill = ({ label, icon: Icon, items = [], selected, onSelect, allLabel, open, onToggle, testId }) => {
  const value = selected?.nome || selected?.label || null;
  return (
    <div className="dna4-pop-wrap">
      <button
        className={`dna4-sel-btn ${value ? 'has-value' : ''} ${open ? 'open' : ''}`}
        onClick={onToggle}
        data-testid={testId}
      >
        <Icon className="ico" />
        <span>{value || label}</span>
        <ChevronDown className="chev" />
      </button>
      {open && (
        <div className="dna4-pop" style={{ minWidth: 220, right: 'auto', left: 0 }}>
          <button
            className="dna4-pop-item"
            onClick={() => { onSelect && onSelect(null); onToggle && onToggle(); }}
            data-testid={`${testId}-all`}
          >
            <span style={{ flex: 1, textAlign: 'left' }}>{allLabel || `Todos ${label}s`}</span>
          </button>
          {items.map((it) => (
            <button
              key={it.id}
              className="dna4-pop-item"
              onClick={() => { onSelect && onSelect(it); onToggle && onToggle(); }}
              data-testid={`${testId}-${it.id}`}
            >
              <span style={{ flex: 1, textAlign: 'left' }}>{it.nome || it.label}</span>
              {selected && selected.id === it.id && (
                <span style={{ color: 'var(--dna-accent)', fontSize: 11, fontWeight: 700 }}>OK</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function DnaHeaderV4({ title, subtitle, actions, theme = 'dark', selectors }) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = readUser();
  const [alertsCount, setAlertsCount] = useState(0);
  const [favs, setFavs] = useState([]);
  const [openMenu, setOpenMenu] = useState(null); // 'favs' | 'profile' | 'unidade' | 'periodo' | 'segmento' | null
  const menuRef = useRef(null);
  const selectorsRef = useRef(null);

  // Polling de alertas + favoritos
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const r = await api.get('/dnacore/v3/alerts');
        if (alive) setAlertsCount(r.data.total || 0);
      } catch { /* noop */ }
      try {
        const r = await api.get('/dnacore/v3/prefs');
        if (alive) setFavs(r.data.favorites || []);
      } catch { /* noop */ }
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // Fecha menus ao clicar fora
  useEffect(() => {
    const onClick = (e) => {
      const insideRight = menuRef.current && menuRef.current.contains(e.target);
      const insideSel = selectorsRef.current && selectorsRef.current.contains(e.target);
      if (!insideRight && !insideSel) setOpenMenu(null);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Atalho Ctrl/⌘+K
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('dna:open-search'));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const crumbs = buildCrumbs(location.pathname);
  const initials = (user.name || user.email || '?').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();

  const logout = () => {
    auditLogout();
    try { localStorage.removeItem('token'); localStorage.removeItem('user'); } catch { /* noop */ }
    navigate('/login');
  };

  return (
    <header className={`dna4-header ${theme === 'light' ? 'theme-light' : ''}`} data-testid="dna4-header">
      <div className="dna4-header-row">
        {/* Crumbs */}
        <nav className="dna4-crumbs" data-testid="dna4-crumbs">
          {crumbs.map((c, i) => {
            const Ico = c.icon;
            const last = i === crumbs.length - 1;
            return (
              <React.Fragment key={c.path}>
                {i > 0 && <ChevronRight className="w-3.5 h-3.5 dna4-crumb-sep" />}
                <button
                  className={`dna4-crumb ${last ? 'active' : ''}`}
                  onClick={() => !last && navigate(c.path)}
                  disabled={last}
                  data-testid={`dna4-crumb-${i}`}
                >
                  {Ico && <Ico className="w-3.5 h-3.5" />} {c.label}
                </button>
              </React.Fragment>
            );
          })}
        </nav>

        {/* Selectors trio (opt-in) */}
        {selectors && (
          <div className="dna4-selectors" ref={selectorsRef}>
            {selectors.unidades && (
              <SelectorPill
                label="Unidade"
                icon={Building2}
                items={selectors.unidades}
                selected={selectors.unidadeSelecionada}
                onSelect={selectors.onUnidadeChange}
                allLabel="Todas Unidades"
                open={openMenu === 'unidade'}
                onToggle={() => setOpenMenu(openMenu === 'unidade' ? null : 'unidade')}
                testId="dna4-sel-unidade"
              />
            )}
            {selectors.periodos && (
              <SelectorPill
                label="Período"
                icon={Calendar}
                items={selectors.periodos}
                selected={selectors.periodoSelecionado}
                onSelect={selectors.onPeriodoChange}
                allLabel="Todos Períodos"
                open={openMenu === 'periodo'}
                onToggle={() => setOpenMenu(openMenu === 'periodo' ? null : 'periodo')}
                testId="dna4-sel-periodo"
              />
            )}
            {selectors.segmentos && (
              <SelectorPill
                label="Segmento"
                icon={Filter}
                items={selectors.segmentos}
                selected={selectors.segmentoSelecionado}
                onSelect={selectors.onSegmentoChange}
                allLabel="Todos Segmentos"
                open={openMenu === 'segmento'}
                onToggle={() => setOpenMenu(openMenu === 'segmento' ? null : 'segmento')}
                testId="dna4-sel-segmento"
              />
            )}
          </div>
        )}

        {/* Right cluster */}
        <div className="dna4-header-actions" ref={menuRef}>
          {/* Busca Global */}
          <button
            className="dna4-icon-btn dna4-search-btn"
            onClick={() => window.dispatchEvent(new CustomEvent('dna:open-search'))}
            data-testid="dna4-search"
            title="Buscar tudo (⌘K)"
          >
            <Search className="w-4 h-4" />
            <span className="dna4-search-lbl">Pesquisar…</span>
            <kbd className="dna4-kbd">⌘K</kbd>
          </button>

          {/* Favoritos */}
          <div className="dna4-pop-wrap">
            <button
              className="dna4-icon-btn"
              onClick={() => setOpenMenu(openMenu === 'favs' ? null : 'favs')}
              data-testid="dna4-favs"
              title="Favoritos"
            >
              <Star className="w-4 h-4" />
              {favs.length > 0 && <span className="dna4-mini-badge">{favs.length}</span>}
            </button>
            {openMenu === 'favs' && (
              <div className="dna4-pop" data-testid="dna4-favs-menu">
                <div className="dna4-pop-head">Favoritos</div>
                {favs.length === 0 ? (
                  <div className="dna4-pop-empty">Nenhum favorito ainda. Marque alunos pela ficha Aluno 360.</div>
                ) : (
                  favs.slice(0, 12).map((f) => (
                    <button
                      key={`${f.type}-${f.id}`}
                      className="dna4-pop-item"
                      onClick={() => { setOpenMenu(null); window.dispatchEvent(new CustomEvent('dna:open-student', { detail: { id: f.id } })); }}
                      data-testid={`dna4-fav-${f.id}`}
                    >
                      <Star className="w-3.5 h-3.5" style={{ color: 'var(--dna-accent-2)' }} />
                      <div style={{ flex: 1, textAlign: 'left' }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{f.label}</div>
                        {f.sub && <div style={{ fontSize: 11, color: 'var(--dna-text-muted)' }}>{f.sub}</div>}
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Alertas */}
          <button
            className="dna4-icon-btn"
            onClick={() => window.dispatchEvent(new CustomEvent('dna:open-alerts'))}
            data-testid="dna4-alerts"
            title="Alertas"
          >
            <Bell className="w-4 h-4" />
            {alertsCount > 0 && <span className="dna4-mini-badge danger">{alertsCount}</span>}
          </button>

          {/* Perfil */}
          <div className="dna4-pop-wrap">
            <button
              className="dna4-profile-btn"
              onClick={() => setOpenMenu(openMenu === 'profile' ? null : 'profile')}
              data-testid="dna4-profile"
              title="Perfil"
            >
              <div className="dna4-avatar">{initials}</div>
            </button>
            {openMenu === 'profile' && (
              <div className="dna4-pop" style={{ minWidth: 240 }} data-testid="dna4-profile-menu">
                <div className="dna4-pop-head" style={{ paddingBottom: 8 }}>
                  <div style={{ fontWeight: 800, fontSize: 14, color: 'var(--dna-text)' }}>{user.name || user.email || 'Usuário'}</div>
                  {user.email && <div style={{ fontSize: 11, color: 'var(--dna-text-muted)' }}>{user.email}</div>}
                  {user.role && <div style={{ fontSize: 10, marginTop: 4, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--dna-accent-2)' }}>{user.role}</div>}
                </div>
                <button className="dna4-pop-item" onClick={() => { setOpenMenu(null); navigate('/admin'); }} data-testid="dna4-profile-settings">
                  <SettingsIcon className="w-4 h-4" /> <span>Configurações</span>
                </button>
                <button className="dna4-pop-item" onClick={() => { setOpenMenu(null); navigate('/v3'); }} data-testid="dna4-profile-home">
                  <Home className="w-4 h-4" /> <span>Centro Neural</span>
                </button>
                <button className="dna4-pop-item danger" onClick={logout} data-testid="dna4-profile-logout">
                  <LogOut className="w-4 h-4" /> <span>Sair</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Title row (opcional) */}
      {(title || actions) && (
        <div className="dna4-header-titlerow">
          <div>
            {title && <h1 className="dna4-page-title">{title}</h1>}
            {subtitle && <p className="dna4-page-sub">{subtitle}</p>}
          </div>
          {actions && <div className="dna4-header-rowactions">{actions}</div>}
        </div>
      )}
    </header>
  );
}
